import { BackEndValueType, ECardinality, ESdChangeReason, IConditionModel, ISymbol } from "@kuika/kuika-cl-model";
import {
  ConditionSetType,
  ESymbolCategory,
  IAction,
  IConditionSet
} from "@kuika/kuika-cl-model/src/models/builder.dtos";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _, { isEqual } from "lodash";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IPureDesignComponentProps, PureSdComponent } from "../../../../shared/pure-sd";
import {
  formatQualifiedName,
  getActionDatasourceQualifier
} from "../../../../shared/utility/datasource-name-qualifier";
import { getResultActionTag } from "../../../../shared/utility/result-action-tag";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import ConditionCascaderSvg from "../../../../svgs/condition-cascader.svg";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { KuikaTag } from "../../../ui-kit/kuika-tag/kuika-tag";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import "./condition.scss";
import { SymbolPicker } from "./symbol-picker";

/** One entry of the operator list a field's type offers. */
export interface IConditionOperator {
  key: string;
  text: string;
  /** "Is Empty" / "Is Not Empty" / "Is Guid" — the comparison has no right-hand side. */
  doesNotNeedSecondField?: boolean;
}

export interface IConditionState {
  isShowModal: boolean;
  selectedCondition: IConditionModel | undefined;
  parentConditionId: string | undefined;
  field1: ISymbol | undefined;
  field2: ISymbol | undefined;
  conditionOperator: string | undefined;
  clickedOrGroupButton?: boolean;
  isModalReady?: boolean;
  initialGroupType?: "and" | "or";
  dragState?: {
    sourceIndex: number;
    destinationIndex: number;
    droppableId: string;
  };
}

export interface IConditionProps {
  action?: IAction;
  condition?: IConditionModel;
  onChange?: (condition: IConditionModel, changeReason?: ESdChangeReason | undefined) => void;
  conditionType:
    | "action"
    | "validation"
    | "visibility"
    | "editability"
    | "workflow"
    | "validationCondition"
    | "parameterValue"
    // KOA-9069 conditional styling. Reuses this builder unchanged, exactly as the visibility
    // and editability panels do — the house pattern for "condition ⇒ visual outcome".
    | "style";
  executePointer?: string;
  showHeader?: boolean;
}

class Condition extends PureSdComponent<IConditionProps & IWithSDProps & IWithContextProp, IConditionState> {
  initialState: IConditionState = {
    isShowModal: false,
    selectedCondition: undefined,
    field1: undefined,
    field2: undefined,
    conditionOperator: undefined,
    parentConditionId: undefined,
    clickedOrGroupButton: undefined,
    initialGroupType: undefined
  };

  constructor(props: IConditionProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      isShowModal: false,
      selectedCondition: undefined,
      field1: undefined,
      field2: undefined,
      conditionOperator: undefined,
      parentConditionId: undefined,
      clickedOrGroupButton: undefined,
      isModalReady: false,
      initialGroupType: undefined
    };

    // Before the first render, not in componentDidMount: the rendered rows already carry the id
    // as their drag handle and their nested-group menu reads it on click. Screen conditions are
    // filled in by preprocessScreen on load, but workflow, validation and parameter-value hosts
    // never pass through it.
    if (props.condition) {
      ScreenDesignHelper.ensureConditionIds(props.condition);
    }
  }

  handleSelectField1 = (symbol: ISymbol, changeReason?: ESdChangeReason | undefined) => {
    this.setState({ field1: _.cloneDeep(symbol) });
  };

  handleSelectField2 = (symbol: ISymbol, changeReason?: ESdChangeReason | undefined) => {
    if (this.state.conditionOperator === "isInList") {
      symbol.cardinality = ECardinality.Many;
    }
    this.setState({ field2: _.cloneDeep(symbol) });
  };

  handleChangeConditionOperator = (operator: IConditionOperator) => {
    if (operator.doesNotNeedSecondField === true) {
      const field2: ISymbol = {
        category: ESymbolCategory.fixed,
        description: "",
        fixedValue: true,
        isFixed: true,
        label: "Fixed Value"
      };

      this.setState({ conditionOperator: operator.key, field2 });
      return;
    }

    if (this.state.field1?.currentType?.toLowerCase() === "boolean") {
      const field2: ISymbol = {
        category: ESymbolCategory.fixed,
        description: "",
        fixedValue: true,
        isFixed: true,
        label: "Fixed Value"
      };

      this.setState({ conditionOperator: operator.key, field2 });
      return;
    }

    this.setState({ conditionOperator: operator.key });
  };

  private isMiCounterSymbol(symbol?: ISymbol | null): boolean {
    if (!symbol) return false;

    const anySym = symbol as any;
    const inputName: string | undefined = anySym?.inputName ?? anySym?.inputname;

    if (!inputName) {
      return false;
    }

    const lc = inputName.toLowerCase();
    return lc === "mi.instancecount" || lc === "mi.completedcount" || lc === "loop.iteration";
  }

  getOperatorsByType(type: BackEndValueType, field1?: ISymbol): IConditionOperator[] {
    const isMiCounter = this.isMiCounterSymbol(field1);
    const typeLc = isMiCounter ? "int32" : type.toLowerCase();
    const operators: IConditionOperator[] = [];
    if (isMiCounter) {
      operators.push({ key: "isEqualTo", text: "Is Equal To" });
      operators.push({ key: "isNotEqualTo", text: "Is Not Equal To" });
      operators.push({ key: "isLessThan", text: "Is Less Than" });
      operators.push({ key: "isLessOrEqual", text: "Is Less Or Equal" });
      operators.push({ key: "isGreaterThan", text: "Is Greater Than" });
      operators.push({ key: "isGreaterOrEqual", text: "Is Greater Or Equal" });
      return operators;
    }
    if (
      typeLc === "unknown" ||
      typeLc === "string" ||
      typeLc === "int16" ||
      typeLc === "int32" ||
      typeLc === "int64" ||
      typeLc === "decimal" ||
      typeLc === "double" ||
      typeLc === "number" ||
      typeLc === "boolean" ||
      typeLc === "date" ||
      typeLc === "datetime" ||
      typeLc === "datetimeoffset" ||
      typeLc === "guid" ||
      typeLc === "image" ||
      typeLc === "colorpicker"
    ) {
      operators.push({ key: "isEqualTo", text: "Is Equal To" });
      operators.push({ key: "isNotEqualTo", text: "Is Not Equal To" });
      operators.push({ key: "isEmpty", text: "Is Empty", doesNotNeedSecondField: true });
      operators.push({ key: "isNotEmpty", text: "Is Not Empty", doesNotNeedSecondField: true });
      operators.push({ key: "isInList", text: "Is In List" });
    }
    if (typeLc === "unknown" || typeLc === "string") {
      operators.push({ key: "startsWith", text: "Starts With" });
      operators.push({ key: "doesNotStartWith", text: "Does Not Start With" });
      operators.push({ key: "contains", text: "Contains" });
      operators.push({ key: "doesNotContain", text: "Does Not Contain" });
      operators.push({ key: "endsWith", text: "Ends With" });
      operators.push({ key: "isShorterOrEqualLength", text: "Length Shorther or Equal To" });
      operators.push({ key: "isShorterLength", text: "Length Shorther To" });
      operators.push({ key: "isLongerLength", text: "Length Longer Then" });
      operators.push({ key: "isLongerOrEqualLength", text: "Length Longer or Equal To" });
      operators.push({ key: "isEqualLength", text: "Length Equal To" });
    }
    if (
      typeLc === "unknown" ||
      typeLc === "int16" ||
      typeLc === "int32" ||
      typeLc === "int64" ||
      typeLc === "decimal" ||
      typeLc === "double" ||
      typeLc === "number"
    ) {
      operators.push({ key: "isLessThan", text: "Is Less Than" });
      operators.push({ key: "isLessOrEqual", text: "Is Less Or Equal" });
      operators.push({ key: "isGreaterThan", text: "Is Greater Than" });
      operators.push({ key: "isGreaterOrEqual", text: "Is Greater Or Equal" });
    }
    if (typeLc === "unknown" || typeLc === "date" || typeLc === "datetime" || typeLc === "datetimeoffset") {
      operators.push({ key: "isEarlierThan", text: "Is Earlier Than" });
      operators.push({ key: "isEarlierOrEqual", text: "Is Earlier Or Equal" });
      operators.push({ key: "isLaterThan", text: "Is Later Than" });
      operators.push({ key: "isLaterOrEqual", text: "Is Later Or Equal" });
    }
    if (typeLc === "string" || typeLc === "guid") {
      operators.push({ key: "isGuid", text: "Is Guid", doesNotNeedSecondField: true });
    }
    return operators;
  }

  getCondition = () => {
    let result: IConditionModel | undefined;
    if (this.props.condition) {
      result = _.cloneDeep(this.props.condition);
      ScreenDesignHelper.migrateConditionSet(result);
    }
    return result;
  };

  onCloseModal = () => {
    this.clearState();
  };

  openFilterModal = (condition?: IConditionModel, initialGroupType?: "and" | "or") => {
    this.setState({
      isShowModal: true,
      selectedCondition: condition,
      field1: condition?.field1,
      field2: condition?.field2,
      conditionOperator: condition?.conditionOperator,
      isModalReady: false,
      initialGroupType
    });
    // The pickers mount a beat after the dialog, so the dialog's own entrance does not pay for the
    // cascader's first render. `requestAnimationFrame` is that beat — but a hidden page never fires
    // one: open the dialog, switch tab or let the window fall behind another, and it comes back with
    // an empty Field box and no way to fill it, because nothing else ever sets this flag. A page that
    // is not being painted has no frame to wait for, so it commits straight away.
    setTimeout(() => {
      if (document.hidden) {
        this.setState({ isModalReady: true });
        return;
      }
      requestAnimationFrame(() => {
        this.setState({ isModalReady: true });
      });
    }, 100);
  };

  getSymbolDescription = (symbol: ISymbol) => {
    let result = symbol.description;

    if (symbol?.actionDefinitionId) {
      const actionDefinition = this.props.sd.actionDefinitions?.find((ad) => ad.id === symbol?.actionDefinitionId);
      if (actionDefinition) {
        const firstIndex = result?.indexOf("[");
        const lastIndex = result?.lastIndexOf("]");
        if (firstIndex > -1 && lastIndex > -1) {
          result = `${result?.substring(0, firstIndex)}[${
            actionDefinition.displayName ?? actionDefinition.name
          }]${result?.substring(lastIndex + 1)}`;
          // "[CRM_DB] [GetUsers].Name" while another datasource holds the same action name
          // (KOA-18147). The row text only: the condition's symbol is left exactly as saved.
          result = formatQualifiedName(
            result,
            getActionDatasourceQualifier(actionDefinition, this.props.sd.actionDefinitions)
          );
        }
      }
    }

    const actionDefinitionRegex = /\[actionDefinitionId:([^\]]+)\]/i;
    const actionDefinitionRegexMatch = symbol.description?.match(actionDefinitionRegex);
    if (actionDefinitionRegexMatch) {
      const actDefId = actionDefinitionRegexMatch[1];
      const actionDefinition = this.props.sd.actionDefinitions.find((x) => x.id === actDefId);
      if (actionDefinition) {
        result = result.replace(actionDefinitionRegex, actionDefinition.displayName ?? actionDefinition.name);
        return result;
      }
    }

    const diIdRegex = /\[diId:([0-9]+)\]/;
    const diIdMatch = symbol.description?.match(diIdRegex);
    if (result && diIdMatch) {
      const matchedDiId = diIdMatch[1];
      const matchedDi = this.props.sd.screenDesignDictionary[parseInt(matchedDiId, 10)];

      if (matchedDi) {
        const matchedDiLabel = matchedDi.friendlyName
          ? matchedDi.friendlyName
          : matchedDi.item.name
            ? matchedDi.item.name
            : matchedDi.item.id
              ? matchedDi.item.id
              : "";
        result = result.replace(diIdRegex, matchedDiLabel);
        return result;
      }
    }

    return result;
  };

  renderConditionDescription = (symbol?: ISymbol): React.ReactNode => {
    if (symbol?.category === ESymbolCategory.fixed) {
      return symbol?.fixedValue?.toString();
    }

    if (symbol?.category === ESymbolCategory.sqlParameter) {
      return symbol?.sqlParamName;
    }

    if (symbol) {
      const description: string = this.getSymbolDescription(symbol);
      // Two results of the same action read alike ("[GetFromBag]"); only the action's tag tells
      // them apart, so the row shows it the way the picker's dropdown does (KOA-16209).
      const actionTag: string | undefined = getResultActionTag(this.props.sd, symbol.resultActionId);
      if (!actionTag) return description;
      return (
        <>
          {description}
          <span className="comparison__condition-tag">
            <KuikaTag type="action" label={actionTag} dataTestId="conditionActionTag" truncate="middle" />
          </span>
        </>
      );
    }

    return "";
  };

  renderConditions = (conditionSet: IConditionSet, parentId?: string) => {
    if (!conditionSet.conditions || conditionSet.conditions.length === 0) {
      return null;
    }

    const droppableId = parentId || "root";

    return (
      <DragDropContext
        onDragStart={this.handleDragStart}
        onDragUpdate={this.handleDragUpdate}
        onDragEnd={(result) => this.handleDragEnd(result, droppableId)}
      >
        <Droppable droppableId={droppableId}>
          {(provided, snapshot) => {
            return (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {conditionSet.conditions.map((condition, index) => (
                  <Draggable key={condition.id} draggableId={condition.id as string} index={index}>
                    {(provided, snapshot) => {
                      return (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="comparison__draggable-item"
                        >
                          <div className="comparison__condition-item-wrapper">
                            <div className="comparison__condition-item">
                              <div style={{ display: "flex", alignItems: "flex-start", gap: 4 }}>
                                <div
                                  {...provided.dragHandleProps}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "grab",
                                    paddingTop: 8
                                  }}
                                >
                                  <KuikaSvg name="grip-vertical" width={12} height={12} color="var(--dark-5)" />
                                </div>
                                <div style={{ flex: 1 }}>
                                  {condition.isOrGroup === true && (
                                    <div className="comparison__or-group-header">
                                      <div style={{ display: "flex", alignItems: "center" }}>
                                        <KuikaBadge
                                          content={
                                            condition.conditionSet?.type === ConditionSetType.and
                                              ? "AND GROUP"
                                              : "OR GROUP"
                                          }
                                          color={"blue"}
                                        />
                                      </div>
                                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <KuikaTooltip content="Add condition" position="bottom">
                                          <KuikaButton
                                            size="sm"
                                            type="text"
                                            isIcon
                                            onClick={() => {
                                              this.openFilterModal(undefined);
                                              this.setState({ parentConditionId: condition.id });
                                            }}
                                          >
                                            <KuikaSvg name="plus" width={14} height={14} color="var(--blue-5)" />
                                          </KuikaButton>
                                        </KuikaTooltip>
                                        <KuikaDropdownMenu
                                          options={[
                                            {
                                              key: "addNestedGroup",
                                              label: "Add Nested Group",
                                              onClick: () => {
                                                this.openFilterModal(undefined);
                                                this.setState({
                                                  parentConditionId: condition.id,
                                                  clickedOrGroupButton: true
                                                });
                                              }
                                            },
                                            {
                                              key: "changeType",
                                              label:
                                                condition.conditionSet?.type === ConditionSetType.and
                                                  ? "Change to OR GROUP"
                                                  : "Change to AND GROUP",
                                              onClick: () => {
                                                this.handleChangeNestedGroupType(condition);
                                              }
                                            },
                                            {
                                              key: "delete",
                                              label: "Delete Group",
                                              danger: true,
                                              onClick: () => {
                                                this.setState({ selectedCondition: condition }, () => {
                                                  this.deleteCondition();
                                                });
                                              }
                                            }
                                          ]}
                                          placement="bottomRight"
                                        >
                                          <KuikaButton
                                            size="sm"
                                            type="text"
                                            isIcon
                                            data-testid="nestedGroupMenuButton"
                                            data-onboarding-id="properties_condition_nested-group-menu"
                                          >
                                            <KuikaSvg name="dots" width={14} height={14} color="var(--dark-7)" />
                                          </KuikaButton>
                                        </KuikaDropdownMenu>
                                      </div>
                                    </div>
                                  )}

                                  <div>
                                    <div>
                                      {condition.conditionSet &&
                                      condition.conditionSet.conditions &&
                                      condition.conditionSet.conditions.length > 0 ? (
                                        <div>{this.renderConditions(condition.conditionSet!, condition.id)}</div>
                                      ) : (
                                        <div
                                          className={classNames("comparison__condition-padding", "cursor-pointer")}
                                          style={{
                                            backgroundColor:
                                              conditionSet.type === ConditionSetType.and ||
                                              conditionSet.type === ConditionSetType.or
                                                ? "var(--dark-2)"
                                                : "var(--dark-0)"
                                          }}
                                          onClick={() => this.openFilterModal(condition)}
                                        >
                                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                            <div className="comparison__condition-header">
                                              <KuikaSmallMediumText className="comparison__condition-title">
                                                {`CONDITION ${index + 1}`}
                                              </KuikaSmallMediumText>
                                            </div>
                                            <div className="comparison__condition-details">
                                              <KuikaSmallText className="comparison__condition-field">
                                                <span style={{ fontWeight: 500 }}>
                                                  {this.renderConditionDescription(condition.field1)}
                                                </span>
                                              </KuikaSmallText>

                                              {condition.conditionOperator && (
                                                <KuikaSmallText className="comparison__condition-operator">
                                                  {condition.conditionOperator}
                                                </KuikaSmallText>
                                              )}

                                              {condition.conditionOperator &&
                                                condition.conditionOperator !== "isEmpty" &&
                                                condition.conditionOperator !== "isNotEmpty" &&
                                                condition.conditionOperator !== "isGuid" && (
                                                  <KuikaSmallText className="comparison__condition-value">
                                                    {this.renderConditionDescription(condition.field2)}
                                                  </KuikaSmallText>
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* <div */}
                            {/*   className="comparison__badge-container" */}
                            {/*   style={{ */}
                            {/*     opacity: this.shouldShowBadgeAfterIndex( */}
                            {/*       index, */}
                            {/*       conditionSet.conditions.length, */}
                            {/*       droppableId */}
                            {/*     ) */}
                            {/*       ? 1 */}
                            {/*       : 0 */}
                            {/*   }} */}
                            {/* > */}
                            {/*   <KuikaBadge */}
                            {/*     content={conditionSet.type === ConditionSetType.and ? "AND" : "OR"} */}
                            {/*     color={"blue"} */}
                            {/*   /> */}
                            {/* </div> */}
                          </div>
                        </div>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            );
          }}
        </Droppable>
      </DragDropContext>
    );
  };

  findConditionById = (condition: IConditionModel, id: string): IConditionModel | undefined => {
    if (condition.id === id) {
      return condition;
    }
    if (condition.conditionSet) {
      for (let i = 0; i < condition.conditionSet.conditions.length; i++) {
        const result = this.findConditionById(condition.conditionSet.conditions[i], id);
        if (result) {
          return result;
        }
      }
    }
    return undefined;
  };

  createCondition = () => {
    let condition = this.getCondition();
    const parentConditionId = this.state.parentConditionId;
    let parentCondition: IConditionModel | undefined;

    if (condition && parentConditionId) {
      parentCondition = this.findConditionById(condition, parentConditionId);
    }

    if (!parentCondition) {
      parentCondition = condition;
    }

    let newCondition: IConditionModel;
    if (this.state.clickedOrGroupButton === true) {
      newCondition = {
        field1: undefined,
        conditionOperator: undefined,
        field2: undefined,
        id: Guid.create().toString(),
        isOrGroup: true,
        conditionSet: {
          type: ConditionSetType.or,
          conditions: [
            {
              field1: this.state.field1,
              conditionOperator: this.state.conditionOperator,
              field2: this.state.field2
                ? this.state.field2
                : this.state.field1?.currentType == "Boolean"
                  ? ({
                      category: ESymbolCategory.fixed,
                      description: "",
                      fixedValue: true,
                      isFixed: true,
                      label: "Fixed Value"
                    } as ISymbol)
                  : undefined,
              id: Guid.create().toString()
            }
          ]
        }
      };
    } else {
      newCondition = {
        field1: this.state.field1,
        conditionOperator: this.state.conditionOperator,
        field2: this.state.field2
          ? this.state.field2
          : this.state.field1?.currentType == "Boolean"
            ? ({
                category: ESymbolCategory.fixed,
                description: "",
                fixedValue: true,
                isFixed: true,
                label: "Fixed Value"
              } as ISymbol)
            : undefined,
        id: Guid.create().toString()
      };
    }

    if (parentCondition === undefined || parentCondition.conditionSet === undefined) {
      return;
    }
    parentCondition.conditionSet.conditions.push(newCondition);
    if (this.props.onChange && condition) {
      this.props.onChange(condition);
    }
    this.clearState();
  };

  updateCondition = () => {
    const condition = this.getCondition();
    const selectedCondition = this.state.selectedCondition;
    selectedCondition!.field1 = this.state.field1;
    selectedCondition!.conditionOperator = this.state.conditionOperator;
    selectedCondition!.field2 = this.state.field2;

    if (condition && selectedCondition && this.props.onChange) {
      this.findAndUpdateCondition(condition, selectedCondition, selectedCondition.id);
      this.clearState();
      this.props.onChange(condition);
    }
  };

  findAndDeleteCondition = (condition: IConditionModel, id?: string) => {
    if (condition.conditionSet) {
      const index = condition.conditionSet.conditions.findIndex((c) => c.id === id);
      if (index > -1) {
        condition.conditionSet.conditions.splice(index, 1);
      } else {
        condition.conditionSet.conditions.forEach((c) => {
          this.findAndDeleteCondition(c, id);
        });
      }
    }
  };

  findAndUpdateCondition = (condition: IConditionModel, newCondition: IConditionModel, id?: string) => {
    if (condition.conditionSet) {
      const index = condition.conditionSet.conditions.findIndex((c) => c.id === id);
      if (index > -1) {
        condition.conditionSet.conditions[index] = newCondition;
      } else {
        condition.conditionSet.conditions.forEach((c) => {
          this.findAndUpdateCondition(c, newCondition, id);
        });
      }
    }
  };

  clearState = () => {
    this.setState({
      selectedCondition: undefined,
      isShowModal: false,
      field1: undefined,
      field2: undefined,
      conditionOperator: undefined,
      clickedOrGroupButton: undefined,
      parentConditionId: undefined,
      isModalReady: false,
      initialGroupType: undefined
    });
  };

  deleteCondition = () => {
    const condition = this.getCondition();
    if (condition) {
      const selectedCondition = this.state.selectedCondition;
      if (selectedCondition) {
        if (this.props.onChange) {
          this.findAndDeleteCondition(condition, selectedCondition.id);
          this.clearState();
          this.props.onChange(condition);
        }
      }
    }
  };

  handleChangeRootGroupType = () => {
    const condition = this.getCondition();
    if (condition && condition.conditionSet) {
      // Toggle between AND and OR
      condition.conditionSet.type =
        condition.conditionSet.type === ConditionSetType.and ? ConditionSetType.or : ConditionSetType.and;
      if (this.props.onChange) {
        this.props.onChange(condition);
      }
    }
  };

  handleChangeNestedGroupType = (groupCondition: IConditionModel) => {
    const condition = this.getCondition();
    if (condition && groupCondition.conditionSet && groupCondition.id) {
      const foundGroup = this.findConditionById(condition, groupCondition.id);
      if (foundGroup && foundGroup.conditionSet) {
        // Toggle between AND and OR
        foundGroup.conditionSet.type =
          foundGroup.conditionSet.type === ConditionSetType.and ? ConditionSetType.or : ConditionSetType.and;
        if (this.props.onChange) {
          this.props.onChange(condition);
        }
      }
    }
  };

  createInitialGroup = (groupType: "and" | "or") => {
    const condition: IConditionModel = {
      field1: undefined,
      conditionOperator: undefined,
      field2: undefined,
      id: Guid.create().toString(),
      conditionSet: {
        type: groupType === "and" ? ConditionSetType.and : ConditionSetType.or,
        conditions: []
      }
    };

    if (this.props.onChange) {
      this.props.onChange(condition);
    }
  };

  reorderConditions = (list: IConditionModel[], startIndex: number, endIndex: number): IConditionModel[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  shouldShowBadgeAfterIndex = (currentIndex: number, totalLength: number, droppableId: string): boolean => {
    // If not dragging, show badges normally (except after last item)
    if (!this.state.dragState || this.state.dragState.droppableId !== droppableId) {
      return currentIndex !== totalLength - 1;
    }

    const { sourceIndex, destinationIndex } = this.state.dragState;

    // Calculate the projected index for this item after the drag
    let projectedIndex = currentIndex;

    if (currentIndex === sourceIndex) {
      // This is the item being dragged - project it to destination
      projectedIndex = destinationIndex;
    } else if (sourceIndex < destinationIndex && currentIndex > sourceIndex && currentIndex <= destinationIndex) {
      projectedIndex = currentIndex - 1;
    } else if (currentIndex >= destinationIndex && currentIndex < sourceIndex) {
      projectedIndex = currentIndex + 1;
    }

    // Show badge if this item won't be the last after reordering
    return projectedIndex !== totalLength - 1;
  };

  handleDragStart = (result: any) => {
    this.setState({
      dragState: {
        sourceIndex: result.source.index,
        destinationIndex: result.source.index,
        droppableId: result.source.droppableId
      }
    });
  };

  handleDragUpdate = (result: any) => {
    if (result.destination) {
      this.setState({
        dragState: {
          sourceIndex: result.source.index,
          destinationIndex: result.destination.index,
          droppableId: result.source.droppableId
        }
      });
    }
  };

  handleDragEnd = (result: DropResult, parentId: string) => {
    // Clear drag state
    this.setState({ dragState: undefined });

    if (!result.destination) {
      return;
    }

    if (result.source.index === result.destination.index) {
      return;
    }

    const condition = this.getCondition();
    if (!condition) {
      return;
    }

    // Find the parent condition set
    let targetConditionSet: IConditionSet | undefined;
    if (parentId === "root") {
      targetConditionSet = condition.conditionSet;
    } else {
      const parentCondition = this.findConditionById(condition, parentId);
      targetConditionSet = parentCondition?.conditionSet;
    }

    if (!targetConditionSet || !targetConditionSet.conditions) {
      return;
    }

    // Reorder the conditions
    targetConditionSet.conditions = this.reorderConditions(
      targetConditionSet.conditions,
      result.source.index,
      result.destination.index
    );

    if (this.props.onChange) {
      this.props.onChange(condition);
    }
  };

  getCategoryName = (symbolPickerNodeCategory: ESymbolCategory): string => {
    switch (symbolPickerNodeCategory) {
      case ESymbolCategory.fixed:
        return "Fixed Value";
      default:
        return ESymbolCategory[symbolPickerNodeCategory];
    }
  };

  getIsNextButtonDisabled = (): boolean => {
    if (!this.state.field1 || _.isEmpty(this.state.field1)) {
      return true;
    }

    if (!this.state.conditionOperator || _.isEmpty(this.state.conditionOperator)) {
      return true;
    }

    // const operators = this.state.field1.currentType ? this.getOperatorsByType(this.state.field1.currentType) : [];
    //
    // const doesNeedSecondField: boolean = !operators.find((x) => x.key === this.state.conditionOperator)
    //   ?.doesNotNeedSecondField;
    //
    // if (
    //   doesNeedSecondField &&
    //   (!this.state.field2 || _.isEmpty(this.state.field2) || this.getIsSymbolFixedValueAndIsEmpty(this.state.field2))
    // ) {
    //   return true;
    // }

    return false;
  };

  getIsSymbolFixedValueAndIsEmpty = (symbol: ISymbol): boolean => {
    if (symbol.category === ESymbolCategory.fixed) {
      return symbol.fixedValue === undefined || symbol.fixedValue === null;
    }

    return false;
  };

  componentDidMount(): void {
    if (this.props.condition && this.getIsConditionNeedToBeMigrated(this.props.condition)) {
      ScreenDesignHelper.migrateConditionSet(this.props.condition);
      this.props.onChange?.(this.props.condition);
    }
  }

  componentDidUpdate(
    prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & IConditionProps & IWithContextProp>
  ): void {
    if (this.props.condition && prevProps.condition !== this.props.condition) {
      ScreenDesignHelper.ensureConditionIds(this.props.condition);
    }

    if (
      prevProps.condition &&
      this.props.condition &&
      this.getIsConditionNeedToBeMigrated(this.props.condition) &&
      _.differenceWith([this.props.condition], [prevProps.condition], isEqual).length > 0
    ) {
      ScreenDesignHelper.migrateConditionSet(this.props.condition);
      this.props.onChange?.(this.props.condition);
    }
  }

  getIsConditionNeedToBeMigrated = (condition: IConditionModel): boolean => {
    if (!condition) return false;

    if (condition.conditionSet === undefined) {
      return true;
    }

    if (condition.conditionSet !== undefined && condition.field1 !== undefined) {
      return true;
    }

    return false;
  };

  render() {
    const condition = this.props.condition;

    let operators: IConditionOperator[] = [];

    if (this.state.field1?.currentType) {
      operators = this.getOperatorsByType(this.state.field1.currentType, this.state.field1);
    }

    const isEdit: boolean = this.state.selectedCondition !== undefined;
    const selectedOperator: IConditionOperator | undefined = operators.find(
      (x: IConditionOperator): boolean => x.key === this.state.conditionOperator
    );
    // Derived from the operator's own metadata rather than a second copy of the "Is Empty /
    // Is Not Empty / Is Guid" list. An operator the current type no longer offers — a stored
    // condition whose field type changed under it — still shows its Value, so it can be edited.
    const operatorNeedsValue: boolean = selectedOperator ? selectedOperator.doesNotNeedSecondField !== true : true;
    const needsValueField: boolean =
      this.state.field1 !== undefined &&
      this.state.field1.currentType !== undefined &&
      this.state.conditionOperator !== undefined &&
      this.state.conditionOperator.length > 0 &&
      operatorNeedsValue;

    return (
      <div className="comparison">
        <div className="comparison__header">
          {this.props.showHeader !== false && (
            <>
              {(this.props.conditionType === "action" || this.props.conditionType === "validationCondition") && (
                <KuikaSmallText style={{ color: "var(--dark-7)" }}>Will execute if...</KuikaSmallText>
              )}
              {this.props.conditionType === "workflow" && (
                <KuikaSmallText style={{ color: "var(--dark-7)" }}>
                  Will execute "{this.props.executePointer}" if...
                </KuikaSmallText>
              )}
              {this.props.conditionType === "validation" && (
                <KuikaSmallText style={{ color: "var(--dark-7)" }}>Will be valid if...</KuikaSmallText>
              )}
              {this.props.conditionType === "visibility" && (
                <KuikaSmallText style={{ color: "var(--dark-7)" }}>Will be visible if...</KuikaSmallText>
              )}
              {this.props.conditionType === "style" && (
                <KuikaSmallText style={{ color: "var(--dark-7)" }}>Will be styled if...</KuikaSmallText>
              )}
            </>
          )}
        </div>

        {condition && condition.conditionSet ? (
          <div className="comparison__filter-container">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
                justifyContent: "space-between"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {/* <KuikaSmallText style={{ color: "var(--dark-7)" }}>Filter</KuikaSmallText> */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <KuikaBadge
                    content={condition.conditionSet.type === ConditionSetType.and ? "AND GROUP" : "OR GROUP"}
                    color={"blue"}
                  />
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <KuikaTooltip content="Add condition" position="bottom">
                  <KuikaButton
                    htmlType="button"
                    type="text"
                    size="sm"
                    isIcon
                    onClick={() => this.openFilterModal(undefined)}
                    data-testid="addConditionToRootButton"
                    data-onboarding-id="properties_condition_add-to-root"
                  >
                    <KuikaSvg name="plus" width={14} height={14} color="var(--blue-5)" />
                  </KuikaButton>
                </KuikaTooltip>
                <KuikaTooltip content="Add nested group" position="bottom">
                  <KuikaButton
                    htmlType="button"
                    type="text"
                    size="sm"
                    isIcon
                    onClick={() => {
                      this.openFilterModal(undefined);
                      this.setState({ clickedOrGroupButton: true });
                    }}
                    data-testid="addNestedGroupToRootButton"
                    data-onboarding-id="properties_condition_add-nested-to-root"
                  >
                    <KuikaSvg name="layers" width={14} height={14} color="var(--blue-5)" />
                  </KuikaButton>
                </KuikaTooltip>
                <KuikaDropdownMenu
                  options={[
                    {
                      key: "addNestedGroup",
                      label: "Add Nested Group",
                      onClick: () => {
                        this.openFilterModal(undefined);
                        this.setState({ clickedOrGroupButton: true });
                      }
                    },
                    {
                      key: "changeType",
                      label:
                        condition.conditionSet.type === ConditionSetType.and
                          ? "Change to OR GROUP"
                          : "Change to AND GROUP",
                      onClick: () => {
                        this.handleChangeRootGroupType();
                      }
                    }
                  ]}
                  placement="bottomRight"
                >
                  <KuikaButton
                    htmlType="button"
                    type="text"
                    size="sm"
                    isIcon
                    data-testid="rootGroupMenuButton"
                    data-onboarding-id="properties_condition_root-group-menu"
                  >
                    <KuikaSvg name="dots" width={14} height={14} color="var(--dark-7)" />
                  </KuikaButton>
                </KuikaDropdownMenu>
              </div>
            </div>

            <div>{this.renderConditions(condition.conditionSet)}</div>
          </div>
        ) : (
          <div className="comparison__condition-content">
            <div style={{ display: "flex", gap: 8, flexDirection: "column" }}>
              <KuikaTooltip content="All conditions must be true for the action to execute" position="right">
                <KuikaButton
                  htmlType="button"
                  type="secondary"
                  className="comparison__filter-button"
                  onClick={() => this.createInitialGroup("and")}
                  data-testid="actionAndGroupButton"
                  data-onboarding-id="properties_condition_and-group"
                >
                  <KuikaSvg name="plus" width={14} height={14} color="var(--blue-5)" />
                  <KuikaSmallText>AND GROUP</KuikaSmallText>
                </KuikaButton>
              </KuikaTooltip>
              <KuikaTooltip content="Any condition can be true for the action to execute" position="right">
                <KuikaButton
                  htmlType="button"
                  type="secondary"
                  className="comparison__filter-button"
                  onClick={() => this.createInitialGroup("or")}
                  data-testid="actionOrGroupButton"
                  data-onboarding-id="properties_condition_or-group"
                >
                  <KuikaSvg name="plus" width={14} height={14} color="var(--blue-5)" />
                  <KuikaSmallText>OR GROUP</KuikaSmallText>
                </KuikaButton>
              </KuikaTooltip>
            </div>
          </div>
        )}

        <KModal
          showModal={this.state.isShowModal}
          closeModal={this.onCloseModal}
          header={isEdit ? "Edit Condition" : "Add Condition"}
          width={400}
          className="comparison__modal"
          footer={
            <KModalFooter
              onDelete={isEdit ? this.deleteCondition : undefined}
              deleteLabel="Remove"
              deleteTestId="addConditionModalDeleteButton"
              deleteOnboardingId="properties_condition_modal-delete"
              onCancel={this.onCloseModal}
              submitLabel={isEdit ? "Save" : "Create"}
              submitTestId={isEdit ? "addConditionModalSaveButton" : "addConditionModalCreateButton"}
              submitOnboardingId={isEdit ? "properties_condition_modal-save" : "properties_condition_modal-create"}
              disabled={this.getIsNextButtonDisabled()}
              onSubmit={isEdit ? this.updateCondition : this.createCondition}
            />
          }
        >
          <div className="comparison__modal-content">
            <div className="comparison__modal-content-field">
              <KuikaText className="comparison__field-label">Field</KuikaText>
              <div className="comparison__condition-container">
                {this.state.isModalReady && (
                  <SymbolPicker
                    action={this.props.action}
                    changeType="onBlur"
                    key={`condition-field1-picker-${this.state.selectedCondition?.id ?? "new"}`}
                    symbol={_.cloneDeep(this.state.field1)}
                    valueType={"Unknown"}
                    targetCardinality={ECardinality.Any}
                    onSelectedSymbol={this.handleSelectField1}
                    showInSingleLine={true}
                    pickerIcon={<ConditionCascaderSvg />}
                    popupPlacement="bottomRight"
                    stablePopup
                    isConditionPicker
                    data_testid="conditionField"
                  ></SymbolPicker>
                )}
              </div>
            </div>

            {this.state.field1 && this.state.field1.currentType && (
              <div className="comparison__modal-content-field">
                <KuikaText className="comparison__field-label">Operator</KuikaText>
                <div className="comparison__select-container">
                  <KuikaSelectbox
                    data-testid="comparisonSelectDropdown"
                    data-onboarding-id="properties_condition_comparison-dropdown"
                    style={{ width: "100%" }}
                    value={this.state.conditionOperator}
                    placeholder="Select"
                    onChange={(value: string) => {
                      const operator = operators.find((x) => x.key === value);
                      if (operator) {
                        this.handleChangeConditionOperator(operator);
                      }
                    }}
                    options={operators.map((operator) => ({
                      value: operator.key,
                      label: operator.text
                    }))}
                  />
                </div>
              </div>
            )}

            {needsValueField && (
              <div className="comparison__modal-content-field">
                <KuikaText className="comparison__field-label">Value</KuikaText>
                <div className="comparison__condition-container">
                  {this.state.isModalReady && (
                    <SymbolPicker
                      action={this.props.action}
                      changeType="onBlur"
                      key={`condition-field2-picker-${this.state.field1?.description ?? ""}-${
                        this.state.field1?.currentType ?? ""
                      }-${this.state.conditionOperator ?? ""}`}
                      symbol={_.cloneDeep(this.state.field2)}
                      valueType={this.state.field1!.currentType!}
                      targetCardinality={
                        this.state.conditionOperator === "isInList"
                          ? ECardinality.Many
                          : this.state.field1!.currentCardinality
                      }
                      onSelectedSymbol={this.handleSelectField2}
                      showInSingleLine={true}
                      pickerIcon={<ConditionCascaderSvg />}
                      popupPlacement="bottomRight"
                      stablePopup
                      isConditionValuePicker
                      data_testid="conditionValue"
                    ></SymbolPicker>
                  )}
                </div>
              </div>
            )}
          </div>
        </KModal>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateEditability ||
      changeReason === ESdChangeReason.UpdateVisibilty ||
      changeReason === ESdChangeReason.UpdateValidation ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange ||
      changeReason === ESdChangeReason.ActionParameterFixedValueChanged
    ) {
      return true;
    }
    return false;
  };
}

const comparison = withContext<IConditionProps>(withSD<IConditionProps>(Condition));
export { comparison as Comparison };
